// MockApiService.ts
// -----------------------------------------------------------------------------
// A complete, FE-only stand-in for APIService. Every method mirrors the real
// service's signature and return shape, but reads/writes localStorage (namespace
// "demo:") as its "backend" and serves static embedded data for status/trend.
//
// It is never imported directly by components. ApiService.tsx wraps its default
// export in a Proxy that forwards to this object whenever isDemoSession() is true,
// so switching to the demo backend is completely transparent to the app.
// -----------------------------------------------------------------------------

import {
  ServiceStatus,
  TrendData,
  FavoriteService,
  MonitorGroup,
  UserProfile,
  MinimalUserProfile,
  RegisterTokenResponse,
  TagConfig,
} from "../Models/Types";
import { DEMO_NS, DEMO_USER_EMAIL } from "./DemoSession";
import {
  buildStatus,
  buildTrend,
  SEED_FAVORITES,
  SEED_GROUPS,
  SEED_USERS,
  SEED_TAGS,
  SEED_SETTINGS,
} from "./DemoData";

// ---------- tiny persistence layer ------------------------------------------

const key = (name: string) => `${DEMO_NS}${name}`;

/** Read a namespaced value, seeding (and persisting) a default the first time. */
function load<T>(name: string, seed: T): T {
  try {
    const raw = localStorage.getItem(key(name));
    if (raw !== null) return JSON.parse(raw) as T;
  } catch {
    /* fall through to seed */
  }
  localStorage.setItem(key(name), JSON.stringify(seed));
  return seed;
}

function save<T>(name: string, value: T): T {
  localStorage.setItem(key(name), JSON.stringify(value));
  return value;
}

// Simulate a little network latency so spinners/loading states are exercised.
const delay = <T,>(value: T, ms = 180): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

// ---------- AUTH (not normally reached in demo, provided for completeness) ---

const ValidateOAuth2Token = async (
  accessToken: string,
): Promise<{ email: string }> => {
  localStorage.setItem("loggedUserId", DEMO_USER_EMAIL);
  localStorage.setItem("sessionToken", accessToken || "DEMO");
  return delay({ email: DEMO_USER_EMAIL });
};

const StartUidLogin = async (): Promise<boolean> => {
  localStorage.setItem("sessionTokenDev", "DEMO-SESSION");
  localStorage.setItem("loggedUserId", DEMO_USER_EMAIL);
  return delay(true);
};

// ---------- USERS & NOTIFICATIONS -------------------------------------------

const GetMyProfile = async (): Promise<MinimalUserProfile> => {
  const allow = load<boolean>("allowNotifications", false);
  return delay({ email: DEMO_USER_EMAIL, role: "admin", allow_notifications: allow });
};

const UpdateNotificationSettings = async (
  allowNotifications: boolean,
): Promise<{ success: boolean }> => {
  save("allowNotifications", allowNotifications);
  return delay({ success: true });
};

const RegisterFCMToken = async (): Promise<RegisterTokenResponse> =>
  delay({ success: true, message: "Token registrato (demo)" });

// ---------- MONITORING (static data, live timestamps) ------------------------

const GetMonitoredServicesStatus = async (): Promise<ServiceStatus[]> =>
  delay(buildStatus());

const GetMonitoredServicesTrend = async (): Promise<TrendData> =>
  delay(buildTrend());

// ---------- FAVORITES --------------------------------------------------------

const getFavorites = (): FavoriteService[] =>
  load<FavoriteService[]>("favorites", SEED_FAVORITES);

const SetIsFavorited = async (
  appId: number,
  favorited: boolean,
): Promise<{ success: boolean; current_favorites: FavoriteService[] }> => {
  let favs = getFavorites();
  if (favorited) {
    if (!favs.some((f) => f.applicationId === appId)) {
      favs = [...favs, { applicationId: appId, ownerName: DEMO_USER_EMAIL }];
    }
  } else {
    favs = favs.filter((f) => f.applicationId !== appId);
  }
  save("favorites", favs);
  return delay({ success: true, current_favorites: favs });
};

const GetAllFavorited = async (): Promise<FavoriteService[]> =>
  delay(getFavorites());

// ---------- GROUPS -----------------------------------------------------------

const getGroups = (): MonitorGroup[] =>
  load<MonitorGroup[]>("groups", SEED_GROUPS);

const GetGroups = async (): Promise<MonitorGroup[]> => delay(getGroups());

const CreateGroup = async (
  groupName: string,
  applicationIdsInGroup: string,
  threshold: number = 10,
): Promise<MonitorGroup> => {
  const groups = getGroups();
  const newGroup: MonitorGroup = {
    id: Date.now(), // unique id, mirrors the real backend's numeric ids
    groupName,
    ownerName: DEMO_USER_EMAIL,
    applicationIdsInGroup,
    threshold,
  };
  save("groups", [...groups, newGroup]);
  return delay(newGroup);
};

const UpdateGroup = async (
  id: number,
  groupName: string,
  applicationIdsInGroup: string,
  threshold: number = 10,
): Promise<MonitorGroup> => {
  const groups = getGroups();
  const updated: MonitorGroup = {
    id,
    groupName,
    ownerName: DEMO_USER_EMAIL,
    applicationIdsInGroup,
    threshold,
  };
  save(
    "groups",
    groups.map((g) => (g.id === id ? updated : g)),
  );
  return delay(updated);
};

const DeleteGroup = async (id: number): Promise<{ success: true }> => {
  save(
    "groups",
    getGroups().filter((g) => g.id !== id),
  );
  return delay({ success: true as const });
};

// ---------- ADMIN: USERS -----------------------------------------------------

const getUsers = (): UserProfile[] => load<UserProfile[]>("users", SEED_USERS);

const AdminGetAllUsers = async (): Promise<UserProfile[]> => delay(getUsers());

const AdminAddUser = async (
  email: string,
  role: string = "user",
  enabled: boolean = true,
): Promise<UserProfile> => {
  const users = getUsers();
  const user: UserProfile = { email, role, tokens: "0", enabled, allow_notifications: false };
  const exists = users.some((u) => u.email === email);
  save("users", exists ? users.map((u) => (u.email === email ? user : u)) : [...users, user]);
  return delay(user);
};

const AdminUpdateUser = async (
  email: string,
  role: string,
  enabled: boolean,
): Promise<UserProfile> => {
  const users = getUsers();
  const existing = users.find((u) => u.email === email);
  const user: UserProfile = {
    email,
    role,
    enabled,
    tokens: existing?.tokens ?? "0",
    allow_notifications: existing?.allow_notifications ?? false,
  };
  save("users", users.map((u) => (u.email === email ? user : u)));
  return delay(user);
};

const AdminDeleteUser = async (email: string): Promise<{ success: true }> => {
  save("users", getUsers().filter((u) => u.email !== email));
  return delay({ success: true as const });
};

// ---------- ADMIN: THRESHOLDS & TAGS ----------------------------------------

const getSettings = () =>
  load<typeof SEED_SETTINGS>("settings", SEED_SETTINGS);

const patchSettings = (patch: Partial<typeof SEED_SETTINGS>) =>
  save("settings", { ...getSettings(), ...patch });

const AdminGetGlobalThreshold = async (): Promise<{ threshold: number }> =>
  delay({ threshold: getSettings().globalThreshold });

const AdminUpdateGlobalThreshold = async (
  threshold: number,
): Promise<{ success: boolean; threshold: number }> => {
  patchSettings({ globalThreshold: threshold });
  return delay({ success: true, threshold });
};

const getTags = (): TagConfig[] => load<TagConfig[]>("tags", SEED_TAGS);

const AdminGetTags = async (): Promise<TagConfig[]> => delay(getTags());

const AdminSetTagThreshold = async (
  tagName: string,
  threshold: number,
  pauseMinutes: number,
): Promise<{ success: boolean }> => {
  const tags = getTags();
  const next: TagConfig = { tag_name: tagName, threshold, pause_minutes: pauseMinutes };
  const exists = tags.some((t) => t.tag_name === tagName);
  save("tags", exists ? tags.map((t) => (t.tag_name === tagName ? next : t)) : [...tags, next]);
  return delay({ success: true });
};

const AdminDeleteTag = async (tagName: string): Promise<{ success: boolean }> => {
  save("tags", getTags().filter((t) => t.tag_name !== tagName));
  return delay({ success: true });
};

// ---------- ADMIN: NOTIFICATION PAUSES --------------------------------------

const AdminGetNotificationPause = async (): Promise<{ pauseMinutes: number }> =>
  delay({ pauseMinutes: getSettings().notificationPause });

const AdminUpdateNotificationPause = async (
  pauseMinutes: number,
): Promise<{ success: boolean; pauseMinutes: number }> => {
  patchSettings({ notificationPause: pauseMinutes });
  return delay({ success: true, pauseMinutes });
};

const AdminGetGroupNotificationPause = async (): Promise<{ pauseMinutes: number }> =>
  delay({ pauseMinutes: getSettings().groupNotificationPause });

const AdminUpdateGroupNotificationPause = async (
  pauseMinutes: number,
): Promise<{ success: boolean; pauseMinutes: number }> => {
  patchSettings({ groupNotificationPause: pauseMinutes });
  return delay({ success: true, pauseMinutes });
};

// ---------- DEV --------------------------------------------------------------

const DevSendPush = async (): Promise<{ message: string }> =>
  delay({ message: "Push di test simulata (demo) — nessun invio reale." });

// -----------------------------------------------------------------------------

const MockApiService = {
  AdminUpdateGroupNotificationPause,
  AdminGetGroupNotificationPause,
  ValidateOAuth2Token,
  AdminGetNotificationPause,
  AdminUpdateNotificationPause,
  StartUidLogin,
  GetMyProfile,
  UpdateNotificationSettings,
  RegisterFCMToken,
  GetMonitoredServicesStatus,
  GetMonitoredServicesTrend,
  SetIsFavorited,
  GetAllFavorited,
  GetGroups,
  CreateGroup,
  UpdateGroup,
  DeleteGroup,
  AdminGetAllUsers,
  AdminAddUser,
  AdminUpdateUser,
  AdminDeleteUser,
  AdminUpdateGlobalThreshold,
  AdminGetGlobalThreshold,
  AdminGetTags,
  AdminDeleteTag,
  AdminSetTagThreshold,
  DevSendPush,
};

export default MockApiService;